import React from "react";
import { motion } from "framer-motion";
import { Users, Layers, GitBranch, Boxes } from "lucide-react";
import Chapter from "../site/Chapter";

const pains = [
  {
    icon: Users,
    title: "Team overload",
    body: "Sourcing, support tickets, admin, reporting — a lean team ends up doing the work of three. Burnout and inconsistency follow.",
    accent: "#00E5FF",
  },
  {
    icon: Layers,
    title: "Tools without ownership",
    body: "ChatGPT, scrapers, chatbots, dashboards — most businesses own AI tools but don't have anyone accountable for running them into finished outcomes.",
    accent: "#A3FF5E",
  },
  {
    icon: GitBranch,
    title: "Inconsistent workflows",
    body: "Every hire, every ticket, every report gets handled a little differently depending on who's free that day. Quality swings with it.",
    accent: "#007AFF",
  },
  {
    icon: Boxes,
    title: "No dedicated operating layer",
    body: "Recruiting, support, and data work often get bolted onto someone's already-full job instead of run as their own function.",
    accent: "#00E5FF",
  },
];

export default function HomeProblem() {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <Chapter
          number="01"
          eyebrow="The Real Problem"
          title={
            <>
              Most businesses don't have a capacity problem.{" "}
              <span className="text-slate-500">They have an</span>{" "}
              <span className="font-serif italic text-white">execution</span>{" "}
              <span className="text-slate-500">problem.</span>
            </>
          }
        />

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.1 }}
          className="mt-6 max-w-3xl space-y-4 text-base sm:text-lg text-slate-400 leading-relaxed"
        >
          <p>
            AI tools can generate lists, drafts, and dashboards. They can't run
            your operations for you.
          </p>
          <p>
            Most growing businesses struggle not because they lack tools, but
            because there's no one accountable for turning tool output into
            finished work — filled roles, resolved tickets, usable data.
          </p>
          <p className="text-slate-300">
            A4 Assist combines AI systems with trained specialists so the work
            actually gets done, not just started.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {pains.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              data-testid={`problem-card-${i}`}
              className="group relative rounded-xl border border-slate-800 bg-[#0A1325]/60 p-7 hover:border-[#00E5FF]/40 transition-colors overflow-hidden"
            >
              <div
                className="absolute -top-12 -right-12 h-32 w-32 rounded-full opacity-[0.08] blur-2xl"
                style={{ background: p.accent }}
              />
              <div className="flex items-start justify-between">
                <div
                  className="inline-flex h-11 w-11 items-center justify-center rounded-lg border"
                  style={{
                    borderColor: `${p.accent}33`,
                    background: `${p.accent}10`,
                    color: p.accent,
                  }}
                >
                  <p.icon size={20} />
                </div>
                <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-slate-600 tabular-nums">
                  0{i + 1}
                </span>
              </div>
              <h3 className="font-display mt-5 text-lg font-semibold text-white">
                {p.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">
                {p.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
